import React, { useCallback, useEffect, useState } from 'react';
import { ApiClient } from '../../api/client';
import { Server, RefreshCw, AlertTriangle, CheckCircle2, Clock } from 'lucide-react';

// ═══════════════════════════════════════════════════════════════
// SelfhostInstancesView – „Self-Hosted Példányok” modul (B7 javítás)
//
// A menüpont (worker/core/manifest.js: selfhost_instances) eddig nem
// rendelkezett nézettel, ezért az App modul-switch a bejegyzéslistára
// esett vissza. Ez a nézet a GET /api/selfhost/instances végpont
// (csak rendszer-superadmin) adatait mutatja: a dockerizált self-host
// telepítések opt-in életjeleit (PHONE_HOME_ENABLED=true).
// Csak olvasás – távoli hozzáférést nem ad a példányokhoz.
// ═══════════════════════════════════════════════════════════════

interface SelfhostInstance {
  id: string;
  org_name?: string | null;
  version?: string | null;
  contact_email?: string | null;
  first_seen?: string | null;
  created_at?: string | null;
  last_seen?: string | null;
}

const STALE_HOURS = 48;

function parseSqlDate(v?: string | null): Date | null {
  if (!v) return null;
  // A D1 datetime('now') formátuma "YYYY-MM-DD HH:MM:SS" (UTC)
  const d = new Date(v.includes('T') ? v : v.replace(' ', 'T') + 'Z');
  return isNaN(d.getTime()) ? null : d;
}

function formatDate(v?: string | null): string {
  const d = parseSqlDate(v);
  return d ? d.toLocaleString('hu-HU') : '–';
}

export const SelfhostInstancesView: React.FC = () => {
  const [items, setItems] = useState<SelfhostInstance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await ApiClient.getSelfhostInstances();
    if (res.ok) {
      const list = (res.data ?? (res as any).results ?? []) as SelfhostInstance[];
      setItems(Array.isArray(list) ? list : []);
    } else {
      setError(res.error || 'A példánylista nem tölthető be');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const now = Date.now();
  const isStale = (i: SelfhostInstance) => {
    const d = parseSqlDate(i.last_seen);
    return !d || now - d.getTime() > STALE_HOURS * 3600 * 1000;
  };

  return (
    <div className="max-w-6xl mx-auto space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400">
            <Server className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900 dark:text-white">Self-Hosted Példányok</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Helyi Docker/VPS telepítések életjelei. Csak azok a példányok jelennek meg, amelyek üzemeltetője bekapcsolta a regisztrációt.
            </p>
          </div>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50 cursor-pointer"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Frissítés
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 rounded-xl border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/30 text-sm text-red-700 dark:text-red-300">
          <AlertTriangle className="w-4 h-4 shrink-0" /> {error}
        </div>
      )}

      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden shadow-xs">
        {loading && !items.length ? (
          <div className="p-8 text-center text-sm text-slate-500">Betöltés…</div>
        ) : !items.length ? (
          <div className="p-8 text-center text-sm text-slate-500 dark:text-slate-400">
            Még egyetlen self-hosted példány sem jelentkezett be.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-950/40 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3">Állapot</th>
                  <th className="px-4 py-3">Szervezet</th>
                  <th className="px-4 py-3">Verzió</th>
                  <th className="px-4 py-3">Kapcsolattartó</th>
                  <th className="px-4 py-3">Utolsó életjel</th>
                  <th className="px-4 py-3">Példány-azonosító</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {items.map((i) => {
                  const stale = isStale(i);
                  return (
                    <tr key={i.id} className="text-slate-700 dark:text-slate-200">
                      <td className="px-4 py-3">
                        {stale ? (
                          <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400"><Clock className="w-4 h-4" /> Nem jelentkezett ({STALE_HOURS} óra+)</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400"><CheckCircle2 className="w-4 h-4" /> Aktív</span>
                        )}
                      </td>
                      <td className="px-4 py-3 font-medium">{i.org_name || '–'}</td>
                      <td className="px-4 py-3 font-mono text-xs">{i.version || '–'}</td>
                      <td className="px-4 py-3">{i.contact_email || '–'}</td>
                      <td className="px-4 py-3 whitespace-nowrap">{formatDate(i.last_seen)}</td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-500 break-all">{i.id}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SelfhostInstancesView;
